/**
 * Source links (TT-1231): one place that knows which live source a URL is.
 *
 * Anything that hands a host app a link — a drop onto the page, a paste, the
 * clipboard on the way into a live start, a link field itself — classifies
 * it here and routes by the kind. The starts stay in the host; this only
 * says which one.
 */

export const SOURCE_LINK_KIND = Object.freeze({
  GMEET: 'gmeet',
  TEAMS: 'teams',
  ZOOM: 'zoom',
  YOUTUBE: 'youtube',
  XSPACE: 'xspace',
});

// Meeting kinds are a live tap of a meeting (a notetaker bot, or the Zoom
// client's RTMS); the rest are a live tap of a stream with a start of their own.
export const MEETING_KINDS = Object.freeze([SOURCE_LINK_KIND.GMEET, SOURCE_LINK_KIND.TEAMS, SOURCE_LINK_KIND.ZOOM]);

const KIND_LABEL = {
  [SOURCE_LINK_KIND.GMEET]: 'Google Meet',
  [SOURCE_LINK_KIND.TEAMS]: 'Microsoft Teams',
  [SOURCE_LINK_KIND.ZOOM]: 'Zoom',
  [SOURCE_LINK_KIND.YOUTUBE]: 'YouTube',
  [SOURCE_LINK_KIND.XSPACE]: 'X Space',
};

// Ordered: the first host test that matches wins, so YouTube's short host
// and X's spaces path sit beside their long forms.
const KIND_TESTS = [
  [SOURCE_LINK_KIND.GMEET, (u) => u.hostname === 'meet.google.com'],
  [SOURCE_LINK_KIND.TEAMS, (u) => /(^|\.)teams\.(microsoft|live)\.com$/.test(u.hostname)],
  [SOURCE_LINK_KIND.ZOOM, (u) => /(^|\.)zoom\.(us|com)$/.test(u.hostname)],
  [
    SOURCE_LINK_KIND.YOUTUBE,
    (u) => /(^|\.)youtube(-nocookie)?\.com$/.test(u.hostname) || u.hostname === 'youtu.be',
  ],
  [
    SOURCE_LINK_KIND.XSPACE,
    (u) => /^(www\.|mobile\.)?(x|twitter)\.com$/.test(u.hostname) && /^\/i\/spaces\//.test(u.pathname),
  ],
];

// Human label for a kind (status lines, detection hints).
export function sourceLinkLabel(kind) {
  return KIND_LABEL[kind] || 'meeting';
}

// The first http(s) URL in a piece of text, or null. A dropped link arrives
// bare; a pasted one often comes wrapped in the sentence it was copied with.
export function extractUrl(text) {
  const match = /https?:\/\/[^\s<>"'`]+/i.exec(text || '');
  if (!match) return null;
  // trailing punctuation belongs to the sentence, not the link
  return match[0].replace(/[.,;:!?)\]]+$/, '');
}

// A link typed without its scheme ("meet.google.com/abc-defg-hij"), as a
// meeting-link field has always accepted: one host-looking token with a
// path, nothing else. The path is what tells it from a file name.
function bareHostUrl(text) {
  const t = (text || '').trim();
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+\/\S+$/i.test(t) ? `https://${t}` : null;
}

/**
 * Classify a piece of text as a live source.
 *
 * Returns `{ kind, url, label }` for a recognized source, `{ kind: null, url }`
 * for a URL with no known start, and null when there is no URL at all.
 */
export function classifySourceLink(text) {
  const url = extractUrl(text) ?? bareHostUrl(text);
  if (!url) return null;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return { kind: null, url };
  }
  const hit = KIND_TESTS.find(([, test]) => test(parsed));
  if (!hit) return { kind: null, url };
  return { kind: hit[0], url, label: KIND_LABEL[hit[0]] };
}

export const isMeetingKind = (kind) => MEETING_KINDS.includes(kind);

/**
 * Prescan the clipboard for a source link (TT-1231). Call it from a click:
 * the read needs the user's activation and, the first time, their permission.
 * Any refusal — no API (Firefox without the permission, an insecure origin,
 * the Zoom client's webview), a denied prompt, an empty clipboard — is null,
 * never an error: the clipboard is a convenience, not an input.
 */
export async function readClipboardSourceLink(clipboard = defaultClipboard()) {
  if (!clipboard?.readText) return null;
  let text;
  try {
    text = await clipboard.readText();
  } catch {
    return null;
  }
  const link = classifySourceLink(text);
  return link?.kind ? link : null;
}

function defaultClipboard() {
  return typeof navigator === 'undefined' ? undefined : navigator.clipboard;
}
